import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Text } from '../../components/ui'
import { toggleSortedNumber } from '../../components/centralAmparo/scheduleHelpers'
import { DatePartSelectors } from './ParametrosFormFields'
import type { DateParts } from '../../components/centralAmparo/parametrosHelpers'

export type HolidayModalState = {
  id?: string
  name: string
  date: DateParts
  all_day: boolean
  hours: number[]
  reason: string
}

type ParametrosHolidayModalProps = {
  holidayModal: HolidayModalState
  holidayError: string | null
  submitting: boolean
  onChange: (next: HolidayModalState) => void
  onClose: () => void
  onSave: () => void
}

const ParametrosHolidayModal: React.FC<ParametrosHolidayModalProps> = ({
  holidayModal,
  holidayError,
  submitting,
  onChange,
  onClose,
  onSave,
}) =>
  createPortal(
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-5">
        <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
          {holidayModal.id ? 'Editar feriado' : 'Novo feriado'}
        </Text>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Fechar
        </Button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nome <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={holidayModal.name}
            onChange={(e) => onChange({ ...holidayModal, name: e.target.value })}
            placeholder="Ex: Natal, Carnaval..."
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <DatePartSelectors
          label="Data (dia / mês / ano)"
          parts={holidayModal.date}
          onChange={(next) => onChange({ ...holidayModal, date: next })}
        />
        <div className="flex items-center gap-2">
          <input
            id="holiday-all-day"
            type="checkbox"
            checked={holidayModal.all_day}
            onChange={(e) =>
              onChange({
                ...holidayModal,
                all_day: e.target.checked,
                hours: e.target.checked ? [] : holidayModal.hours,
              })
            }
          />
          <label htmlFor="holiday-all-day" className="text-sm text-gray-700 dark:text-gray-300">
            Dia inteiro
          </label>
        </div>
        {!holidayModal.all_day && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Horas bloqueadas <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onChange({
                      ...holidayModal,
                      hours: Array.from({ length: 24 }, (_, i) => i),
                    })
                  }
                >
                  Selecionar todos
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange({ ...holidayModal, hours: [] })}
                >
                  Limpar
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <label
                  key={h}
                  className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md border text-sm cursor-pointer select-none ${
                    holidayModal.hours.includes(h)
                      ? 'border-primary-500 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={holidayModal.hours.includes(h)}
                    onChange={() =>
                      onChange({
                        ...holidayModal,
                        hours: toggleSortedNumber(holidayModal.hours, h),
                      })
                    }
                  />
                  {h.toString().padStart(2, '0')}h
                </label>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Motivo / observação
          </label>
          <input
            type="text"
            value={holidayModal.reason}
            onChange={(e) => onChange({ ...holidayModal, reason: e.target.value })}
            placeholder="Opcional"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      {holidayError && (
        <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{holidayError}</Text>
        </div>
      )}
      <div className="flex justify-end gap-3 mt-6">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="solid" onClick={onSave} disabled={submitting}>
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  </div>,
  document.body
  )

export default ParametrosHolidayModal
